import { useState } from "react";
import { Plus } from "lucide-react";
import { Loader } from "@/components/shared/loader";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useCreateDocumentLibrary } from "@/features/documents/hooks/useDocumentLibrariesQueries";
import type { DepartmentOption } from "./DocumentsFilters";

const NONE = "NONE";

interface CreateLibraryDialogProps {
  departments: DepartmentOption[];
  /** Pre-select department (e.g. scoped Sub-Admins). */
  lockedDepartmentId?: string;
}

export function CreateLibraryDialog({
  departments,
  lockedDepartmentId,
}: CreateLibraryDialogProps) {
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState("");
  const [departmentId, setDepartmentId] = useState(lockedDepartmentId ?? "");
  const create = useCreateDocumentLibrary();

  function resetForm() {
    setTitle("");
    setDepartmentId(lockedDepartmentId ?? "");
  }

  async function onCreate() {
    const trimmed = title.trim();
    if (!trimmed) return;
    try {
      await create.mutateAsync({
        title: trimmed,
        ...(departmentId ? { departmentId } : {}),
      });
      setOpen(false);
      resetForm();
    } catch {
      /* handled by mutation */
    }
  }

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) resetForm();
        else setDepartmentId(lockedDepartmentId ?? departmentId);
      }}
    >
      <DialogTrigger asChild>
        <Button>
          <Plus className="w-4 h-4 ml-2" />
          مكتبة جديدة
        </Button>
      </DialogTrigger>
      <DialogContent className="max-w-md">
        <DialogHeader className="text-right">
          <DialogTitle>إنشاء مكتبة مستندات</DialogTitle>
          <DialogDescription>
            أضف مكتبة جديدة لتنظيم المستندات ضمن دائرة معيّنة.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-1 text-right">
            <Label htmlFor="library-title">الاسم</Label>
            <Input
              id="library-title"
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="مثال: التقارير السنوية"
            />
          </div>
          <div className="space-y-1 text-right">
            <Label>الدائرة</Label>
            <Select
              value={departmentId || NONE}
              onValueChange={(v) => setDepartmentId(v === NONE ? "" : v)}
              disabled={!!lockedDepartmentId}
            >
              <SelectTrigger>
                <SelectValue placeholder="اختر الدائرة" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>بدون دائرة</SelectItem>
                {departments.map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <Button
            className="w-full"
            onClick={onCreate}
            disabled={!title.trim() || create.isPending}
          >
            إنشاء
            {create.isPending && <Loader className="mr-2" />}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}
